// src/pages/HistoricalContent/HistoricalContent.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Snackbar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import PageTitle from '../PageTitle/PageTitle';
import DirectoryTree from '../../components/DirectoryTree/DirectoryTree';
import DocumentViewModal from '../../components/DocumentViewModal';

import './HistoricalContent.css';

const HISTORICAL_ROOT_PATH = 'Archive/AMF Documentation Archive';
const HISTORICAL_ROOT_NORMALIZED = HISTORICAL_ROOT_PATH.replace(/^\/+|\/+$/g, '');

const HistoricalContent = () => {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedFilePath, setSelectedFilePath] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 300);
        return () => clearTimeout(timer);
    }, []);

    const handleFileClick = (filePath, fileName) => {
        if (!filePath) return;
        setSelectedFilePath(filePath);
        setSelectedFileName(fileName || filePath.split('/').pop());
        setModalOpen(true);
    };

    if (loading) {
        return (
            <div className="historical-page">
                <div className="historical-loading">
                    <CircularProgress size={28} sx={{ color: 'var(--theme-blue-main)' }} />
                    <Typography className="historical-loading-text">
                        {t('HISTORICAL_CONTENT.LOADING')}
                    </Typography>
                </div>
            </div>
        );
    }

    return (
        <div className="historical-page">
            <div className="historical-content">
                {/* Page title */}
                <PageTitle title={t('HISTORICAL_CONTENT.TITLE')} />

                {/* Error snackbar */}
                {error && (
                    <Snackbar
                        open={!!error}
                        autoHideDuration={6000}
                        onClose={() => setError(null)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert
                            severity="error"
                            onClose={() => setError(null)}
                            className="error-alert"
                        >
                            {error}
                        </Alert>
                    </Snackbar>
                )}

                {/* Directory tree container */}
                <div className="historical-tree-wrapper">
                    <DirectoryTree
                        rootPath={HISTORICAL_ROOT_PATH}
                        onFileClick={handleFileClick}
                    />
                </div>
            </div>

            {/* File preview modal */}
            <DocumentViewModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedFilePath(null);
                    setSelectedFileName(null);
                }}
                filePath={selectedFilePath}
                fileName={selectedFileName}
                archiveRootPath={HISTORICAL_ROOT_NORMALIZED}
            />
        </div>
    );
};

export default HistoricalContent;
